import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2Icon, XIcon } from "lucide-react";
import { bookingUrl } from "../data/content";
type BookingModalProps = {
  open: boolean;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;

// Calendly colours matched to the site palette (warm white, anthracite text, berry accent).
const src = `${bookingUrl}?hide_gdpr_banner=1&background_color=f9f7f4&text_color=24272b&primary_color=8e3b55`;
export function BookingModal({
  open,
  onClose
}: BookingModalProps) {
  // The calendar starts loading in the background shortly after the page is ready,
  // and stays loaded, so opening the pop-up is (nearly) instant.
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    addPreconnect("https://calendly.com");
    addPreconnect("https://assets.calendly.com");
    const start = () => setShouldLoad(true);
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: {
        timeout: number;
      }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    const timer = window.setTimeout(() => {
      if (w.requestIdleCallback) idleId = w.requestIdleCallback(start, {
        timeout: 2000
      });else start();
    }, 1500);
    return () => {
      window.clearTimeout(timer);
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    setShouldLoad(true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" initial={false} animate={open ? {
    opacity: 1,
    visibility: "visible"
  } : {
    opacity: 0,
    transitionEnd: {
      visibility: "hidden"
    }
  }} style={{
    visibility: "hidden",
    opacity: 0,
    pointerEvents: open ? "auto" : "none"
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} aria-hidden={!open} data-id="element-35" data-mp="ujm9o8pgphiz">
      <div className="absolute inset-0 bg-espresso/70" onClick={onClose} aria-hidden="true" data-id="element-36" data-mp="18hkkx58hkxu9" />
      <motion.div role="dialog" aria-modal="true" aria-label="Kostenloses Gespräch buchen" initial={false} animate={open ? {
      opacity: 1,
      scale: 1,
      y: 0
    } : {
      opacity: 0,
      scale: 0.96,
      y: 8
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="relative flex h-[min(820px,92svh)] w-full max-w-4xl flex-col overflow-hidden rounded-[1.5rem] bg-cream shadow-2xl" data-id="element-37" data-mp="13ihfhjolgplr">
        <div className="flex items-center justify-between border-b border-cream-line px-6 py-4" data-id="element-38" data-mp="1ys3478uodnbf">
          <p className="font-serif text-2xl text-espresso" data-id="element-39" data-mp="23q4p7zbnvwpj">Kostenloses Gespräch buchen</p>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Schließen" tabIndex={open ? 0 : -1} className="flex h-10 w-10 items-center justify-center rounded-full text-cocoa transition-colors duration-150 hover:bg-cream-deep hover:text-espresso focus:outline-none focus-visible:ring-2 focus-visible:ring-bark" data-id="element-40" data-mp="28q0s1v8mhi3i">
            <XIcon className="h-5 w-5" data-id="element-41" data-mp="2rfz04bcf7b75" />
          </button>
        </div>
        <div className="relative flex-1" data-id="element-42" data-mp="2wfbkmpftk66k">
          {!loaded && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-taupe" data-id="element-43" data-mp="2wek14czhxywn">
              <Loader2Icon className="h-6 w-6 animate-spin" data-id="element-44" data-mp="x4bvxy7n1ye3" />
              <span className="text-sm" data-id="element-45" data-mp="122dcabdojjlv">Kalender wird geladen …</span>
            </div>}
          {shouldLoad && <iframe src={src} title="Termin mit dem Team von Eva-Maria Bast buchen" onLoad={() => setLoaded(true)} tabIndex={open ? 0 : -1} className={`h-full w-full border-0 transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`} data-id="element-46" data-mp="2sgl18plswlg6" />}
        </div>
      </motion.div>
    </motion.div>;
}
function addPreconnect(href: string) {
  if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = href;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}